import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { ThreeCircles } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import { imgBaseurl } from '../../baseUrl/baseurl.ts';
import { virtualMallSingleIdProductPurchaseApi, virtualMallSingleIdProductTransactionIdCheckApi, virtualMallSingleIdProductsApi } from './VirtualMallApi/api';
import './VirtualMallSingleIdProducts.scss';

const emptyPurchaseForm = {
    full_name: '', mobile: '', address: '', pincode: '', state: '', district: '',
    taluk: '', corporation: '', panchayat: '', transaction_id: '', transaction_screenshot: null
};

export default function VirtualMallSingleIdProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [purchaseForm, setPurchaseForm] = useState(emptyPurchaseForm);
    const [submitting, setSubmitting] = useState(false);
    const [transactionCheck, setTransactionCheck] = useState({ value: '', state: 'idle', message: '' });

    useEffect(() => {
        const loadProducts = async () => {
            try {
                const response = await virtualMallSingleIdProductsApi();
                setProducts(response?.status && Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Failed to load single ID products:', error);
                toast.error('Failed to load single ID products.');
            } finally {
                setLoading(false);
            }
        };
        loadProducts();
    }, []);

    const openPurchaseForm = (product) => {
        setSelectedProduct(product);
        setPurchaseForm(emptyPurchaseForm);
        setTransactionCheck({ value: '', state: 'idle', message: '' });
    };

    const closePurchaseForm = () => {
        if (submitting) return;
        setSelectedProduct(null);
        setPurchaseForm(emptyPurchaseForm);
        setTransactionCheck({ value: '', state: 'idle', message: '' });
    };

    const updatePurchaseField = (event) => {
        const { name } = event.target;
        let { value } = event.target;
        if (name === 'mobile') value = value.replace(/\D/g, '').slice(0, 10);
        if (name === 'pincode') value = value.replace(/\D/g, '').slice(0, 6);
        if (name === 'transaction_id') {
            value = value.replace(/\D/g, '').slice(0, 12);
            setTransactionCheck({ value, state: 'idle', message: '' });
        }
        setPurchaseForm((current) => ({ ...current, [name]: value }));
    };

    const checkTransactionId = async () => {
        const transactionId = purchaseForm.transaction_id;
        if (!/^\d{12}$/.test(transactionId)) {
            setTransactionCheck({ value: transactionId, state: 'idle', message: '' });
            return false;
        }
        setTransactionCheck({ value: transactionId, state: 'checking', message: 'Checking transaction ID…' });
        try {
            const response = await virtualMallSingleIdProductTransactionIdCheckApi(transactionId);
            if (!response?.status) throw new Error(response?.message || 'Could not validate transaction ID');
            const state = response.exists ? 'duplicate' : 'available';
            setTransactionCheck({ value: transactionId, state, message: response.message });
            return response.exists ? 'duplicate' : 'available';
        } catch (error) {
            setTransactionCheck({ value: transactionId, state: 'error', message: 'Could not check this ID. Please try again.' });
            return 'error';
        }
    };

    const submitPurchase = async (event) => {
        event.preventDefault();
        if (!selectedProduct) return;
        if (!/^\d{10}$/.test(purchaseForm.mobile)) {
            toast.error('Please enter a valid 10-digit mobile number.');
            return;
        }
        if (!/^\d{6}$/.test(purchaseForm.pincode)) {
            toast.error('Please enter a valid 6-digit pincode.');
            return;
        }
        if (!/^\d{12}$/.test(purchaseForm.transaction_id)) {
            toast.error('Transaction ID must contain exactly 12 digits.');
            return;
        }
        if (transactionCheck.value !== purchaseForm.transaction_id || transactionCheck.state !== 'available') {
            const validation = await checkTransactionId();
            if (validation !== 'available') {
                if (validation === 'duplicate') toast.error('This transaction ID has already been used. Please enter a different ID.');
                else toast.error('Please check the transaction ID before submitting.');
                return;
            }
        }
        if (!purchaseForm.transaction_screenshot) {
            toast.error('Please upload the transaction screenshot.');
            return;
        }

        const payload = new FormData();
        payload.append('single_product_id', selectedProduct.single_product_id);
        Object.entries(purchaseForm).forEach(([key, value]) => {
            if (key !== 'transaction_screenshot' && value !== '') payload.append(key, value);
        });
        payload.append('transaction_screenshot', purchaseForm.transaction_screenshot);

        setSubmitting(true);
        try {
            const response = await virtualMallSingleIdProductPurchaseApi(payload);
            if (!response?.status) {
                toast.error(response?.message || 'Could not submit the purchase request.');
                return;
            }
            toast.success(response.message || 'Purchase request submitted.');
            setSelectedProduct(null);
            setPurchaseForm(emptyPurchaseForm);
            setTransactionCheck({ value: '', state: 'idle', message: '' });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not submit the purchase request.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="dashboard-section single-id-products-section">
            <h2>Single ID Products</h2>
            {loading ? (
                <div className="single-id-products-loading">
                    <ThreeCircles height="64" width="64" color="#667eea" visible />
                </div>
            ) : products.length ? (
                <div className="single-id-products-grid">
                    {products.map((product) => (
                        <article className="single-id-product-card" key={product.single_product_id}>
                            <div className="single-id-product-image">
                                <img
                                    src={`${imgBaseurl}/${product.product_image}`}
                                    alt={product.product_name}
                                    onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }}
                                />
                            </div>
                            <div className="single-id-product-details">
                                <h3>{product.product_name}</h3>
                                <div className="single-id-product-amounts">
                                    <div>
                                        <span>Shop Purchase Amount</span>
                                        <strong>₹{Number(product.shop_purchase_amount).toFixed(2)}</strong>
                                    </div>
                                    <div>
                                        <span>SWVP Amount</span>
                                        <strong>{Number(product.swvp_amount).toFixed(2)}</strong>
                                    </div>
                                </div>
                                <button type="button" className="single-id-buy-now" onClick={() => openPurchaseForm(product)}>
                                    Buy Now
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            ) : (
                <div className="single-id-products-empty">No single ID products are available right now.</div>
            )}

            {selectedProduct && (
                <div className="single-id-checkout-overlay" onMouseDown={(event) => {
                    if (event.target === event.currentTarget) closePurchaseForm();
                }}>
                    <section className="single-id-checkout-modal" role="dialog" aria-modal="true" aria-labelledby="single-id-checkout-title">
                        <header className="single-id-checkout-header">
                            <div>
                                <h2 id="single-id-checkout-title">Purchase {selectedProduct.product_name}</h2>
                                <p>Shop Purchase Amount: ₹{Number(selectedProduct.shop_purchase_amount).toFixed(2)}</p>
                            </div>
                            <button type="button" className="single-id-checkout-close" onClick={closePurchaseForm} disabled={submitting} aria-label="Close">
                                <X size={22} />
                            </button>
                        </header>
                        <form className="single-id-checkout-form" onSubmit={submitPurchase}>
                            <div className="single-id-checkout-fields">
                                <label className="single-id-field single-id-field-full">
                                    Full Name *
                                    <input required name="full_name" value={purchaseForm.full_name} onChange={updatePurchaseField} autoComplete="name" />
                                </label>
                                <label className="single-id-field">
                                    Mobile Number *
                                    <input required name="mobile" type="tel" inputMode="numeric" value={purchaseForm.mobile} onChange={updatePurchaseField} autoComplete="tel" />
                                </label>
                                <label className="single-id-field single-id-field-full">
                                    Address *
                                    <textarea required name="address" rows="3" value={purchaseForm.address} onChange={updatePurchaseField} autoComplete="street-address" />
                                </label>
                                <label className="single-id-field">
                                    Pincode *
                                    <input required name="pincode" inputMode="numeric" value={purchaseForm.pincode} onChange={updatePurchaseField} autoComplete="postal-code" />
                                </label>
                                <label className="single-id-field">
                                    State *
                                    <input required name="state" value={purchaseForm.state} onChange={updatePurchaseField} autoComplete="address-level1" />
                                </label>
                                <label className="single-id-field">
                                    District *
                                    <input required name="district" value={purchaseForm.district} onChange={updatePurchaseField} />
                                </label>
                                <label className="single-id-field">
                                    Taluk *
                                    <input required name="taluk" value={purchaseForm.taluk} onChange={updatePurchaseField} />
                                </label>
                                <label className="single-id-field">
                                    Corporation
                                    <input name="corporation" value={purchaseForm.corporation} onChange={updatePurchaseField} />
                                </label>
                                <label className="single-id-field">
                                    Panchayat
                                    <input name="panchayat" value={purchaseForm.panchayat} onChange={updatePurchaseField} />
                                </label>
                                <label className="single-id-field single-id-field-full">
                                    Transaction ID *
                                    <input required name="transaction_id" inputMode="numeric" value={purchaseForm.transaction_id} onChange={updatePurchaseField} onBlur={checkTransactionId} aria-describedby="single-id-transaction-check" />
                                    {transactionCheck.message && transactionCheck.value === purchaseForm.transaction_id && <small id="single-id-transaction-check" className={`single-id-transaction-check is-${transactionCheck.state}`} role="status">{transactionCheck.message}</small>}
                                </label>
                                <label className="single-id-field single-id-field-full">
                                    Transaction Screenshot *
                                    <input required name="transaction_screenshot" type="file" accept="image/*" onChange={(event) => setPurchaseForm((current) => ({ ...current, transaction_screenshot: event.target.files?.[0] || null }))} />
                                </label>
                            </div>
                            <footer className="single-id-checkout-actions">
                                <button type="button" className="single-id-cancel-button" onClick={closePurchaseForm} disabled={submitting}>Cancel</button>
                                <button type="submit" className="single-id-submit-button" disabled={submitting}>
                                    {submitting ? 'Submitting...' : 'Submit Order'}
                                </button>
                            </footer>
                        </form>
                    </section>
                </div>
            )}
        </section>
    );
}
